import {
    useMemo,
} from 'react'
import useHasPermission from 'app/hooks/useHasPermission'

import StatusHandler from 'app/shared-components/StatusHandler'
import {
    ArchiveButton,
    ArchiveConfirmationDialog,
    useArchive,
} from 'app/shared-components/Archive'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import request from 'app/requests/Contact/Contact.requests'

import ContactForm from '../ContactForm'
import useGetContact from '../hooks/useGetContact'
import useUpdateContact from '../hooks/useUpdateContact'
import useHasContactPermissionType from '../../utils/useHasContactPermissionType'

const ContactUpdate = ({
    id,
}: { id: number }) => {
    const {
        Contacts: ContactsRoutes,
    } = useContactManagementRoutes()
    const {
        data,
        isDataReady,
        error,
        isError,
        invalidate,
        isFetching,
    } = useGetContact(id)

    const archive = useArchive({
        requestParamFn: data?.archived ? request.restore
            : request.archive,
        notificationId: 'Contact',
        title: 'Contact',
        data,
        redirectionAfterSuccess: generateSkyNetLink({
            domainPath: data?.isProspect ? ContactsRoutes.Prospect.path : ContactsRoutes.path,
        }),
    })

    const updateContact = useUpdateContact({
        onSuccess: invalidate,
    })

    const hasEditPermissions = useHasPermission(['location_sales'])
    const hasEditTypesPermissions = useHasContactPermissionType(data?.mainType, 'edit')

    const calculatedForbiddenEdit = useMemo(() => {
        if (!data?.mainType?.length) {
            return false
        }

        return data?.isProspect ? !hasEditPermissions : !hasEditTypesPermissions
    }, [
        data?.isProspect,
        data?.mainType,
        hasEditPermissions,
        hasEditTypesPermissions,
    ])

    const customButtons = useMemo(() => {
        return !calculatedForbiddenEdit ? [<ArchiveButton
            key="archive-button"
            onClick={archive.handleArchiveRestore}
            archived={data?.archived}
        />] : []
    }, [
        calculatedForbiddenEdit,
        archive,
        data,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <ContactForm
                    data={data}
                    disabled={calculatedForbiddenEdit}
                    action={updateContact}
                    onCancel={invalidate}
                    customButtons={customButtons}
                />
                <ArchiveConfirmationDialog
                    title="Contact"
                    openConfirmDialog={archive.openConfirmDialog}
                    handleClose={archive.handleArchiveRestoreConfirm}
                    archived={data?.archived}
                />
            </>
        </StatusHandler>
    )
}

export default ContactUpdate
